import _ from 'lodash'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/macro'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import { Alert, Grid, Stack } from '@mui/material'
import colors from '@/assets/scss/_themes-vars.module.scss'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import TabPanel from '@/components/ui/tab/TabPanel'
import { ConsultationStatus } from '@/core/models/consultation.model'
import { OfflineDocuments } from '@/features/offline/documents'
import ConsultationHeader from '@/pages/consultation/components/ConsultationHeader'
import PatientProfile from '@/pages/consultation/components/PatientProfile'
import VitalData from '@/pages/consultation/containers/VitalData'
import { useOfflineMode } from '@/providers/OfflineModeProvider'
import { OfflineInitializing, OfflineStatusIndicator } from '@/shared/offline'

import { useOfflineConsultationLoader } from '../model/useOfflineConsultationLoader'
import { useMenuActions } from '../model/useMenuActions'

type OfflineConsultationPageProps = {}

const defaultSizes = [60, 40]

export function OfflineConsultationPage(props: OfflineConsultationPageProps) {
  const lingui = useLingui()
  const navigate = useNavigate()
  const { consultationId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const { isOfflineDatabaseReady } = useOfflineMode()

  const { consultation, patient, error, loading, setConsultation } = useOfflineConsultationLoader({ consultationId, isOfflineDatabaseReady })

  const tabs = useMemo(
    () =>
      _.compact([
        { name: 'patientProfile', label: t('page.consultation.tab.patientProfile') },
        { name: 'vitalData', label: t('page.consultation.tab.vitalData') },
        { name: 'documents', label: t('page.consultation.tab.documents') },
      ]),
    [lingui, t]
  )
  const [currentTab, setCurrentTab] = useState<string>(tabs[0].name)

  const handleSelectTab = (tab: { name: string; label: string }) => () => {
    setCurrentTab(tab.name)
  }

  const menuActions = useMenuActions({ consultation, setConsultation })

  if (!isOfflineDatabaseReady) {
    return <OfflineInitializing />
  }

  if (loading) {
    return (
      <Container maxWidth="xl">
        <OfflineStatusIndicator />
        <Stack flex={1} alignItems="center" justifyContent="center" sx={{ height: '400px' }}>
          <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
        </Stack>
      </Container>
    )
  }

  if (error || !consultation) {
    return (
      <Container maxWidth="xl">
        <OfflineStatusIndicator />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Alert severity="error">{error || 'Consultation not found'}</Alert>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl">
      <OfflineStatusIndicator />
      <Box sx={{ pt: '20px', pb: '20px' }}>
        <Grid container>
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            sx={{
              marginBottom: '20px',
            }}
          >
            <ConsultationHeader
              actions={menuActions}
              isDoctor={false} // In offline mode, assume limited permissions
              consultation={consultation as any} // Cast to match expected type
            />
          </Grid>
          <Grid container spacing={3}>
            {tabs.map((tab) => (
              <Grid item xs={12} sm={6} md={4} lg={12 / tabs.length} key={tab.name}>
                <Button
                  fullWidth
                  onClick={handleSelectTab(tab)}
                  sx={[
                    {
                      color: colors.gray500,
                      fontSize: '14px',
                      fontWeight: 500,
                    },
                    tab.name === currentTab && {
                      backgroundColor: colors.indigo100,
                      color: colors.indigo700,
                      '&:hover': {
                        backgroundColor: colors.indigo100,
                      },
                    },
                  ]}
                >
                  {tab.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Box sx={{ mt: '28px' }}>
          {consultation?.status === ConsultationStatus.Completed && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('page.consultation.closed.message')}
            </Alert>
          )}
          {consultation?._syncStatus === 'pending' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Trans>This consultation is offline and will be synced when connection is restored.</Trans>
            </Alert>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TabPanel value="patientProfile" index={currentTab}>
                <PatientProfile consultation={consultation as any} />
              </TabPanel>
              <TabPanel value="vitalData" index={currentTab}>
                <VitalData consultationId={consultation._id} />
              </TabPanel>
              <TabPanel value="documents" index={currentTab}>
                <OfflineDocuments consultation={consultation} onConsultationUpdate={setConsultation} />
              </TabPanel>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container>
  )
}
